import React, { useRef } from "react";
import { IMaskInput } from "react-imask";

type TextInputProps = {
  name: string;
  label: string;
  onChange: any;
  placeholder: string;
  value: string;
  error: any;
  disabled: boolean;
  className: string;
  type: string;
  initialFocus: boolean;
  onKeyPress?: (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  maxLength?: number;
  rows?: number;
  min?: number;
};

const TextInput = ({
  name,
  label,
  onChange,
  placeholder,
  value,
  error,
  disabled,
  className,
  type,
  min,
  initialFocus,
  onKeyPress,
  maxLength,
  rows,
}: TextInputProps) => {
  let wrapperClass = "form-group w-full";
  if (error && error.length > 0) {
    wrapperClass += " has-error";
  }

  const defaultClassName =
    "form-control rounded px-2 py-1 border-0 " +
    (disabled
      ? "rounded px-2 mr-0 md:mr-3 py-2 border-0 text-charcoal-900 dark:text-dustyGray-200 bg-dustyGray-200 dark:bg-dustyGray-800 transition duration-300"
      : "rounded px-2 py-2 border-0 text-charcoal-900 dark:text-dustyGray-300 bg-dustyGray-100 dark:bg-dustyGray-700 transition duration-300");

  const initialRef = useRef(null);

  // Use masking for wildXRNumber only
  const isMasked = name === "wildXRNumber";
  const mask = isMasked ? "*****-*****" : null;

  // Handle paste events for masked inputs
  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (isMasked) {
      const pastedText = e.clipboardData.getData("text");
      const syntheticEvent = {
        target: {
          name,
          value: pastedText,
        },
      };
      onChange(syntheticEvent);
      e.preventDefault();
    }
  };

  const sharedProps = {
    name,
    className: className || defaultClassName,
    placeholder,
    value: value || "",
    onChange,
    disabled,
    ref: initialFocus ? initialRef : null,
    onKeyUp: onKeyPress,
    maxLength: maxLength || (isMasked ? 11 : undefined),
    onPaste: handlePaste,
  };

  let inputElement;

  if (rows) {
    // Textarea
    inputElement = <textarea {...sharedProps} rows={rows} />;
  } else if (isMasked && mask) {
    // Masked input — only spread non-conflicting props to child input
    inputElement = (
      <IMaskInput
        mask={mask}
        value={value || ""}
        onChange={onChange}
        disabled={disabled}
        inputRef={initialFocus ? initialRef : null}
        name={name}
        type="text"
        min={min}
        className={className || defaultClassName}
        placeholder={placeholder}
        onKeyUp={onKeyPress}
        onPaste={handlePaste}
      />
    );
  } else {
    // Standard input
    inputElement = <input type={type || "text"} {...sharedProps} />;
  }

  return (
    <div className={wrapperClass}>
      <label htmlFor={name} className="transition duration-300 text-black">
        {label}
      </label>
      <div className="field">{inputElement}</div>
      {error && <p className="text-red-500 text-xs italic">{error}</p>}
    </div>
  );
};

export default TextInput;
